import moment from 'moment';
import { Modes } from './modes';

export const CLOSING_BEFORE = {
  TWENTY_FOUR_HOURS: 'TWENTY_FOUR_HOURS',
  SEVEN_DAYS: 'SEVEN_DAYS',
  THIRTY_DAYS: 'THIRTY_DAYS',
  NINETY_DAYS: 'NINETY_DAYS',
  ANY_TIME: 'ANY_TIME',
};

export const CLOSING_BEFORE_VALUES = Object.values(CLOSING_BEFORE);

export const STATUS = {
  active: 'active',
  canceled: 'canceled',
  complete: 'complete',
  unsuccessful: 'unsuccessful',
  planned: 'planned',
  planning: 'planning',
  withdrawn: 'withdrawn',
};

export const STATUS_VALUES = Object.values(STATUS);

export const MATCH_CONTEXT = {
  DESCRIPTION: 'DESCRIPTION',
  TITLE: 'TITLE',
};

export const MATCH_CONTEXT_VALUES = Object.values(MATCH_CONTEXT);

export const ZONES = {
  CANADA: {
    name: ['Canada'],
    zones: {
      ALBERTA: ['Alberta'],
      BRITISH_COLUMBIA: ['British Columbia', 'Colombie-Britannique'],
      PRINCE_EDWARD_ISLAND: ['Prince Edward Island', 'Île-du-Prince-Edouard'],
      MANITOBA: ['Manitoba'],
      NEW_BRUNSWICK: ['New Brunswick', 'Nouveau-Brunswick'],
      NOVA_SCOTIA: ['Nova Scotia', 'Nouvelle-Écosse', 'Nouvelle Écosse'],
      NUNAVUT: ['Nunavut'],
      ONTARIO: ['Ontario'],
      QUEBEC: ['Quebec', 'Québec'],
      SASKATCHEWAN: ['Saskatchewan'],
      NEWFOUNDLAND_LABRADOR: [
        'Newfoundland and Labrador',
        'NewFoundland and Labrador',
        'NewFoundland & Labrador',
        'Terre-Neuve-et-Labrador',
      ],
      NORTHWEST_TERRITORIES: ['Northwest Territories', 'Territoires du Nord-Ouest'],
      YUKON: ['Yukon'],
    },
  },
  UNITED_STATES: {
    name: ['United States', 'États-Unis', 'USA'],
    zones: {},
  },
};

export const SEAO_ADMINISTRATIVE_REGIONS = {
  BAS_ST_LAURENT: ['Bas-Saint-Laurent', 'Bas-St-Laurent'],
  SAGUENAY_LAC_ST_JEAN: ['Saguenay–Lac-Saint-Jean', 'Saguenay–Lac-St-Jean'],
  CAPITAL_NATIONALE: ['Capitale-Nationale'],
  MAURICIE: ['Mauricie'],
  ESTRIE: ['Estrie'],
  MONTREAL: ['Montréal'],
  OUTAOUAIS: ['Outaouais'],
  ABITIBI_TEMISCAMINGUE: ['Abitibi-Témiscamingue'],
  COTE_NORD: ['Côte-Nord'],
  NORD_DU_QUEBEC: ['Nord-du-Québec'],
  GASPESIE_ILES_DE_LA_MADELEINE: ['Gaspésie–Îles-de-la-Madeleine'],
  CHAUDIERE_APPALACHES: ['Chaudière-Appalaches'],
  LAVAL: ['Laval'],
  LANAUDIERE: ['Lanaudière'],
  LAURENTIDES: ['Laurentides'],
  MONTEREGIE: ['Montérégie'],
  CENTRE_DU_QUEBEC: ['Centre-du-Québec'],
};

// SEAO administrative regions round-trip (mirrors the zones expand/contract pattern): the app
// stores/edits enum keys (e.g. MAURICIE), but the search endpoint matches on the display values
// (e.g. Mauricie). One key can map to several accepted spellings.
const SEAO_REGION_VALUE_TO_KEY = Object.entries(SEAO_ADMINISTRATIVE_REGIONS).reduce((acc, [key, values]) => {
  values.forEach((value) => {
    acc[value] = key;
  });
  return acc;
}, {});

// Enum keys -> display values, for the search payload / stored view (`{seao_v2: ['Mauricie', ...]}`).
export const expandSeaoRegions = (administrativeRegions = {}) => {
  const ar = administrativeRegions || {};
  const keys = ar['seao_v2'];
  if (!keys || !keys.length) return ar;
  return { ...ar, seao_v2: keys.map((key) => SEAO_ADMINISTRATIVE_REGIONS[key] || [key]).flat() };
};

// Display values -> enum keys, for the filter editor / SEAO dialog checkboxes (deduped, since
// several values map back to one key). Tolerant: unknown entries pass through unchanged.
export const contractSeaoRegions = (administrativeRegions = {}) => {
  const ar = administrativeRegions || {};
  const values = ar['seao_v2'];
  if (!values || !values.length) return ar;
  return { ...ar, seao_v2: [...new Set(values.map((value) => SEAO_REGION_VALUE_TO_KEY[value] || value))] };
};

export const ZONES_CANADA_KEYS = Object.keys(ZONES.CANADA.zones);
export const ZONES_USA_KEYS = Object.keys(ZONES.UNITED_STATES.zones);
export const COUNTRIES = Object.keys(ZONES);

export const CATEGORIES = {
  SERVICES: 'services',
  GOODS: 'goods',
  WORKS: 'works',
  OTHERS: 'others',
};

export const READ_FILTERS = {
  READ: 'read',
  UNREAD: 'unread',
};

export const SHARED_FILTERS = {
  READ: 'shared',
  UNREAD: 'unshared',
};

export const FOLLOWED_FILTERS = {
  READ: 'followed',
  UNREAD: 'unfollowed',
};

export const DOCUMENT_FILTERS = {
  WITH_DOCUMENT: true,
  WITHOUT_DOCUMENT: false,
};

export const CATEGORIES_VALUES = Object.values(CATEGORIES);
export const READ_FILTERS_VALUES = Object.values(READ_FILTERS);
export const SHARED_FILTERS_VALUES = Object.values(SHARED_FILTERS);
export const FOLLOWED_FILTERS_VALUES = Object.values(FOLLOWED_FILTERS);
export const DOCUMENT_FILTERS_VALUES = Object.values(DOCUMENT_FILTERS);
export const mapToSearchParameters = (searchFilters, mode) => {
  // Pull administrativeRegions out of `rest` so it's never spread raw — it's re-added below only
  // when there are values (otherwise it must be omitted, not sent as empty/enum-key data).
  const { closingBefore, keywords, administrativeRegions, ...rest } = searchFilters;

  // SEAO regions are stored as enum keys but the search endpoint matches on display values.
  // Reuse the shared round-trip helper (same mapping the tenders-views flow uses) instead of
  // duplicating it here. Currently only SEAO is mapped; other platforms will follow when supported.
  const expandedRegions = expandSeaoRegions(administrativeRegions || {});
  const hasRegions = !!(expandedRegions['seao_v2'] && expandedRegions['seao_v2'].length);

  return {
    closingBefore: mapClosingBefore(closingBefore),
    keywords: mode === Modes.EXPLORE ? searchFilters.keywords : null,
    ...rest,
    // Only include SEAO administrativeRegions when there are values
    ...(hasRegions ? { administrativeRegions: expandedRegions } : {}),
  };
};

export const mapClosingBefore = (closingBefore) => {
  switch (closingBefore) {
    case CLOSING_BEFORE.TWENTY_FOUR_HOURS:
      return moment().add(1, 'days').utc();
    case CLOSING_BEFORE.SEVEN_DAYS:
      return moment().add(7, 'days').utc();
    case CLOSING_BEFORE.THIRTY_DAYS:
      return moment().add(30, 'days').utc();
    case CLOSING_BEFORE.NINETY_DAYS:
      return moment().add(90, 'days').utc();
    case CLOSING_BEFORE.ANY_TIME:
      return moment().add(999999, 'days').utc();
    default:
      return undefined;
  }
};
